import { useCallback, useMemo, useState } from "react";
import { Box, Flex, HStack, Text } from "@chakra-ui/react";
import { useReq } from "@larner.dev/use-req";
import {
  emailEnabledForCategory,
  NOTIFICATION_CONSENT_DISCLOSURE_HASH,
  NotificationConsentPayload,
  notificationCategories,
  NotificationPreferences,
  NotificationPreferenceType,
  textEnabledForCategory,
  textNotificationSupportedForCategory,
  UserAttributesUpdate,
  UserWithMediaAndGroups,
  withEmailEnabled,
  withTextEnabled,
} from "@greenflagdate/shared";
import { Icon, type IconName } from "../../../components/Icon/Icon";
import { apiReq } from "../../../lib/apiReq";
import { store, useStoreData } from "../../../lib/store/store";
import { ActionType } from "../../../lib/store/storeActions";
import { toaster } from "../../../lib/toaster";
import { ModalPage } from "../../../components/ModalPage";
import { ChannelSwitch } from "../../../components/ChannelSwitch/ChannelSwitch";
import { textOptInConsent } from "../../../lib/textOptIn";
import { VerifyPhoneNumberDialog } from "../../../components/VerifyPhoneNumberDialog/VerifyPhoneNumberDialog";
import { EnableTextConsentDialog } from "../../../components/EnableTextConsentDialog/EnableTextConsentDialog";

const CATEGORY_ICONS: Record<NotificationPreferenceType, IconName> = {
  [NotificationPreferenceType.EventReminders]: "cake",
  [NotificationPreferenceType.Matches]: "heart",
  [NotificationPreferenceType.SparkInvitations]: "spark-filled",
  [NotificationPreferenceType.NewEvents]: "bell",
};

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <Text
      as="div"
      fontSize="10px"
      letterSpacing="0.14em"
      textTransform="uppercase"
      color="fg.muted"
      fontWeight="semibold"
      mb="10px"
    >
      {children}
    </Text>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <Box
      bg="bg.canvas"
      border="1px solid"
      borderColor="border.subtle"
      borderRadius="sm"
      boxShadow="card"
      overflow="hidden"
    >
      {children}
    </Box>
  );
}

// Which category the user asked to text-enable, and which gate it is waiting
// on. null means no text-enable is in flight.
type TextEnableFlow = {
  step: "verifyPhone" | "consent";
  category: NotificationPreferenceType;
} | null;

export function Notification() {
  const storeData = useStoreData();
  const user = storeData.user;

  const [updateUserReq, updateUserReqState] =
    useReq<UserWithMediaAndGroups>(apiReq);

  const prefs: Partial<NotificationPreferences> = useMemo(
    () => user?.notification_preferences ?? {},
    [user?.notification_preferences]
  );

  // Consent on file for the current disclosure lets text be (re)enabled
  // without prompting again.
  const hasConsentOnFile =
    user?.notification_consent_hash === NOTIFICATION_CONSENT_DISCLOSURE_HASH;
  const hasPhone = !!user?.phone_number;

  // Turning text on for a category can need two things first: a verified phone
  // and agreement to the disclosure. One state value holds which category the
  // user tapped and which gate it is waiting on, so the tap is never lost and
  // no combination of independent flags can describe a step that doesn't exist.
  const [textEnableFlow, setTextEnableFlow] = useState<TextEnableFlow>(null);

  const putPrefs = useCallback(
    async (
      partial: Partial<NotificationPreferences>,
      consent?: NotificationConsentPayload
    ) => {
      const body: UserAttributesUpdate = {
        notification_preferences: partial,
        notification_consent: consent,
      };
      const response = await updateUserReq.put("/user/attributes", body);
      if (response.success) {
        store.dispatch({
          type: ActionType.UpdateUser,
          params: { user: response.result },
        });
        return true;
      }
      toaster.create({
        type: "error",
        title: "Couldn't save your notification preferences.",
      });
      return false;
    },
    [updateUserReq]
  );

  const setEmail = useCallback(
    (type: NotificationPreferenceType, on: boolean) => {
      putPrefs({ [type]: withEmailEnabled(prefs[type], on) });
    },
    [prefs, putPrefs]
  );

  const setText = useCallback(
    (type: NotificationPreferenceType, on: boolean) => {
      if (!on) {
        putPrefs({ [type]: withTextEnabled(prefs[type], false) });
        return;
      }
      // No phone → verify it first; the consent step follows. Consent already
      // on file → enable straight away. Otherwise → ask for consent.
      if (!hasPhone) {
        setTextEnableFlow({ step: "verifyPhone", category: type });
      } else if (hasConsentOnFile) {
        putPrefs({ [type]: withTextEnabled(prefs[type], true) });
      } else {
        setTextEnableFlow({ step: "consent", category: type });
      }
    },
    [prefs, putPrefs, hasPhone, hasConsentOnFile]
  );

  const confirmConsent = useCallback(async () => {
    if (textEnableFlow?.step !== "consent") return;
    const { category } = textEnableFlow;
    const ok = await putPrefs(
      { [category]: withTextEnabled(prefs[category], true) },
      textOptInConsent("settings")
    );
    if (ok) setTextEnableFlow(null);
  }, [textEnableFlow, prefs, putPrefs]);

  const saving = updateUserReqState.loading;

  return (
    <ModalPage
      motionKey="settings-notification-modal"
      title="Notification Preferences"
      fallback="/settings"
      bodyBg="bg.canvas"
    >
    <Flex direction="column" minH="100%" w="100%">
      <Box flex="1" w="100%" maxW="40rem" mx="auto" pt="18px" pb="2rem">
        <Box px="4" mb="2">
          <Text fontSize="13px" color="fg.muted" lineHeight="1.5">
            Choose how we reach you for each category — by email, text, or both.
          </Text>
        </Box>

        <Box px="4" pt="4">
          <Eyebrow>Notifications</Eyebrow>
          <Card>
            {notificationCategories.map((category, i) => {
              const current = prefs[category.type];
              const emailOn = emailEnabledForCategory(current);
              const textOn = textEnabledForCategory(current);
              // New Events is a marketing announcement, not an account
              // notification, so it's email-only — no text switch is offered.
              const textSupported = textNotificationSupportedForCategory(
                category.type
              );
              const icon = CATEGORY_ICONS[category.type] ?? "bell";
              return (
                <Flex
                  key={category.type}
                  align="center"
                  gap="14px"
                  p="14px 16px"
                  borderTop={i === 0 ? "0" : "1px solid"}
                  borderTopColor="border.subtle"
                >
                  <Flex
                    w="36px"
                    h="36px"
                    borderRadius="full"
                    align="center"
                    justify="center"
                    flexShrink={0}
                    bg="bg.brandSoft"
                    color="fg.brand"
                  >
                    <Icon name={icon} boxSize="16px" />
                  </Flex>
                  <Box flex={1} minW="0">
                    <Text
                      fontSize="14px"
                      fontWeight="semibold"
                      color="fg.default"
                      lineHeight="1.25"
                    >
                      {category.title}
                    </Text>
                    <Text
                      fontSize="12px"
                      color="fg.muted"
                      lineHeight="1.4"
                      mt="2px"
                    >
                      {category.description}
                    </Text>
                  </Box>
                  <HStack gap="14px" flexShrink={0}>
                    <ChannelSwitch
                      label="Email"
                      checked={emailOn}
                      onChange={() => setEmail(category.type, !emailOn)}
                      disabled={saving}
                      ariaLabel={`Email for ${category.title}`}
                    />
                    <ChannelSwitch
                      label="Text"
                      checked={textOn}
                      onChange={() => setText(category.type, !textOn)}
                      disabled={saving}
                      ariaLabel={`Text for ${category.title}`}
                      unavailableText={textSupported ? undefined : "Email only"}
                    />
                  </HStack>
                </Flex>
              );
            })}
          </Card>
        </Box>

        {updateUserReqState.error && (
          <Box px="4" pt="3">
            <HStack
              gap="2"
              p="10px 12px"
              bg="#FEF5F5"
              border="1px solid"
              borderColor="fg.danger"
              borderRadius="xs"
            >
              <Icon name="x" boxSize="14px" color="fg.danger" />
              <Text fontSize="12px" color="fg.danger">
                {updateUserReqState.error.code ||
                  "Something went wrong saving your changes."}
              </Text>
            </HStack>
          </Box>
        )}
      </Box>
    </Flex>

      <VerifyPhoneNumberDialog
        isShown={textEnableFlow?.step === "verifyPhone"}
        // Phone verified: advance the same category to the consent step.
        onSuccess={() =>
          setTextEnableFlow((flow) =>
            flow ? { step: "consent", category: flow.category } : null
          )
        }
        // Abandoned: drop the category too, so the consent dialog doesn't pop
        // for one we can't text-enable.
        onCancel={() => setTextEnableFlow(null)}
      />
      <EnableTextConsentDialog
        isShown={textEnableFlow?.step === "consent"}
        onConfirm={confirmConsent}
        onClose={() => setTextEnableFlow(null)}
        loading={saving}
      />
    </ModalPage>
  );
}
